import { useState } from 'react';
import { MailWarning, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/lib/auth';
import { ApiError, resendVerificationRequest } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const DISMISS_KEY = 'ls.verifyEmailBanner.dismissed';

function isDismissed(): boolean {
  try {
    return sessionStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Quiet, non-blocking banner shown to logged-in users whose email is not yet
 * verified (#84). Dismissible for the session only (sessionStorage).
 */
export function VerifyEmailBanner() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(isDismissed);
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);

  if (!user || user.emailVerified !== false || dismissed) return null;

  const knownEmail = user.email?.trim() || '';

  const send = async (target: string) => {
    setSending(true);
    try {
      await resendVerificationRequest(target);
      toast.success(t('verifyEmail.resendSuccess'));
    } catch (error) {
      if (error instanceof ApiError && error.status === 429) {
        toast.error(t('verifyEmail.resendRateLimited'));
      } else {
        toast.error(t('verifyEmail.resendSuccess'));
      }
    } finally {
      setSending(false);
    }
  };

  const onResendClick = () => {
    if (knownEmail) void send(knownEmail);
  };

  const dismiss = () => {
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // sessionStorage unavailable — dismiss in memory only
    }
    setDismissed(true);
  };

  return (
    <div
      role="region"
      aria-label={t('verifyEmail.bannerTitle')}
      className="border-b border-status-pending/25 bg-status-pending/[0.07] px-4 py-2.5 lg:px-10"
    >
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <span className="flex min-w-0 items-center gap-2.5">
          <MailWarning className="h-4 w-4 shrink-0 text-status-pending" aria-hidden="true" />
          <p className="text-sm leading-snug text-foreground">
            <span className="font-medium">{t('verifyEmail.bannerTitle')}</span>{' '}
            <span className="text-muted-foreground">{t('verifyEmail.bannerBody')}</span>
          </p>
        </span>

        <span className="flex items-center gap-2">
          {!knownEmail && (
            <Input
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('verifyEmail.resendPlaceholder')}
              aria-label={t('verifyEmail.resendLabel')}
              className="h-8 w-52 text-sm"
              disabled={sending}
            />
          )}
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="h-8"
            disabled={sending || (!knownEmail && !email.trim())}
            onClick={knownEmail ? onResendClick : () => void send(email.trim())}
          >
            {sending ? (
              <>
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                {t('verifyEmail.resending')}
              </>
            ) : (
              t('verifyEmail.resend')
            )}
          </Button>
          <button
            type="button"
            onClick={dismiss}
            aria-label={t('verifyEmail.dismiss')}
            className="grid h-8 w-8 place-items-center rounded-[7px] text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </span>
      </div>
    </div>
  );
}
